// /client/lib/dexscreener.ts

// ============================================================
// Shared token list (used by BottomMarquee and other UI bits)
// ============================================================
export const COMMON_TOKENS = {
  solana: {
    SOL: "So11111111111111111111111111111111111111112",
    USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
    USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
    FIXERCOIN: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump",
    PINGX: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump",
    LOCKER: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump",
    FXM: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump",
  },
};

export const CHAIN_IDS = {
  solana: "solana",
};

export const TOP_MARKETS = [
  "SOL/USDT",
  "FIXERCOIN/USDT",
  "PINGX/USDT",
  "LOCKER/USDT",
  "FXM/USDT",
];

export interface ResolvedToken {
  tokenAddress: string;
  chain: string;
}

export function resolveSymbolToToken(symbol: string): ResolvedToken | null {
  const [baseToken] = symbol.split("/");
  if (baseToken in COMMON_TOKENS.solana) {
    return {
      tokenAddress:
        COMMON_TOKENS.solana[baseToken as keyof typeof COMMON_TOKENS.solana],
      chain: CHAIN_IDS.solana,
    };
  }
  return null;
}

// ============================================================
// Public types
// ============================================================
export interface TokenPriceData {
  price: number;
  change24h: number;
  symbol: string;
  address: string;
  network?: string;
}

export interface Trade {
  id: string;
  type: "buy" | "sell";
  price: number;
  amount: number;
  total: number;
  timestamp: string;
  maker: string;
}

export interface Holder {
  rank: number;
  address: string;
  balance: number;
  percentage: number;
}

// ============================================================
// Token address tables
// ============================================================
const SOLANA_ADDRESSES = {
  SOL: "So11111111111111111111111111111111111111112",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  FIXERCOIN: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump",
  LOCKER: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump",
  FXM: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump",
  PINGX: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump",
};

const EVM_ADDRESSES: Record<string, Record<string, string>> = {
  ethereum: {
    ETH: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
    USDC: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
    USDT: "0xdac17f958d2ee523a2206206994597c13d831ec7",
  },
  bsc: {
    BNB: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c",
    USDC: "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d",
    USDT: "0x55d398326f99059ff775485246999027b3197955",
  },
  polygon: {
    MATIC: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270",
    USDC: "0x2791bca1f2de4661ed88a30c99a7a9449aa84174",
    USDT: "0xc2132d05d31c914a87c6611c10748aeb04b58e8f",
  },
  mintme: {
    MINTME: "native",
    FIXERCOIN: "0x091da08c5bf888252ed1ab3e44246cbf72d63307",
  },
};

const FALLBACK_PRICES: Record<string, { price: number; change24h: number }> = {
  SOL: { price: 150, change24h: 0 },
  USDC: { price: 1, change24h: 0 },
  USDT: { price: 1, change24h: 0 },
  FIXERCOIN: { price: 0.00005, change24h: 0 },
  LOCKER: { price: 0.00001, change24h: 0 },
  FXM: { price: 0.000015, change24h: 0 },
  PINGX: { price: 0.00001, change24h: 0 },
  ETH: { price: 3000, change24h: 0 },
  BNB: { price: 600, change24h: 0 },
  MATIC: { price: 0.8, change24h: 0 },
  MINTME: { price: 0.01, change24h: 0 },
};

// ============================================================
// Price cache
// ============================================================
let priceCache: Map<
  string,
  { price: number; change24h: number; timestamp: number }
> = new Map();
const CACHE_TTL = 60000;

function getTokenSymbolFromAddress(address: string): string {
  const solanaSymbols: Record<string, string> = {
    [SOLANA_ADDRESSES.SOL]: "SOL",
    [SOLANA_ADDRESSES.USDC]: "USDC",
    [SOLANA_ADDRESSES.USDT]: "USDT",
    [SOLANA_ADDRESSES.FIXERCOIN]: "FIXERCOIN",
    [SOLANA_ADDRESSES.PINGX]: "PINGX",
    [SOLANA_ADDRESSES.LOCKER]: "LOCKER",
    [SOLANA_ADDRESSES.FXM]: "FXM",
  };
  for (const network of Object.keys(EVM_ADDRESSES)) {
    for (const [symbol, addr] of Object.entries(EVM_ADDRESSES[network])) {
      if (addr.toLowerCase() === address.toLowerCase()) return symbol;
    }
  }
  return solanaSymbols[address] || address.slice(0, 6);
}

// ============================================================
// getTokenPrice
// ============================================================
export async function getTokenPrice(
  identifier: string,
  network: string = "solana"
): Promise<TokenPriceData | null> {
  if (!identifier) return null;

  const cacheKey = `${network}:${identifier}`;
  const cached = priceCache.get(cacheKey);
  if (cached && cached.timestamp > Date.now() - CACHE_TTL) {
    return {
      price: cached.price,
      change24h: cached.change24h,
      symbol: getTokenSymbolFromAddress(identifier),
      address: identifier,
      network,
    };
  }

  if (
    identifier === "USDC" ||
    identifier === "USDT" ||
    identifier === SOLANA_ADDRESSES.USDC ||
    identifier === SOLANA_ADDRESSES.USDT
  ) {
    const result = {
      price: 1.0,
      change24h: 0,
      symbol: identifier === "USDC" ? "USDC" : "USDT",
      address: identifier,
      network,
    };
    priceCache.set(cacheKey, { price: 1.0, change24h: 0, timestamp: Date.now() });
    return result;
  }

  const symbolMap: Record<string, { chain: string; address: string }> = {
    ETH: { chain: "ethereum", address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2" },
    BNB: { chain: "bsc", address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c" },
    MATIC: { chain: "polygon", address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270" },
    SOL: { chain: "solana", address: SOLANA_ADDRESSES.SOL },
  };

  let addressToFetch = identifier;
  let chainToUse = network;
  if (symbolMap[identifier]) {
    addressToFetch = symbolMap[identifier].address;
    chainToUse = symbolMap[identifier].chain;
  }

  try {
    const url = `https://api.dexscreener.com/latest/dex/tokens/${addressToFetch}`;
    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "User-Agent": "Mozilla/5.0 (compatible; FixoriumWallet/1.0)",
      },
    });
    if (!response.ok) return getFallbackPrice(identifier);
    const data = await response.json();
    if (!data?.pairs || data.pairs.length === 0) return getFallbackPrice(identifier);

    let filteredPairs = data.pairs;
    if (chainToUse && chainToUse !== "solana") {
      filteredPairs = data.pairs.filter((p: any) => p.chainId === chainToUse);
    }
    if (filteredPairs.length === 0) filteredPairs = data.pairs;

    const bestPair = filteredPairs.sort(
      (a: any, b: any) =>
        parseFloat(b.liquidity?.usd || "0") - parseFloat(a.liquidity?.usd || "0")
    )[0];
    if (!bestPair) return getFallbackPrice(identifier);

    let price = 0;
    let change24h = 0;
    if (bestPair.baseToken?.address === addressToFetch) {
      price = parseFloat(bestPair.priceUsd || "0");
    } else if (bestPair.quoteToken?.address === addressToFetch) {
      const basePrice = parseFloat(bestPair.priceUsd || "0");
      if (basePrice > 0) price = 1 / basePrice;
    } else {
      price = parseFloat(bestPair.priceUsd || "0");
    }
    if (bestPair.priceChange?.h24 !== undefined) {
      change24h = parseFloat(bestPair.priceChange.h24);
    } else if (bestPair.priceChange24h !== undefined) {
      change24h = parseFloat(bestPair.priceChange24h);
    }
    if (isNaN(price) || price <= 0) return getFallbackPrice(identifier);

    const symbol = getTokenSymbolFromAddress(addressToFetch);
    const result = {
      price,
      change24h,
      symbol,
      address: addressToFetch,
      network: chainToUse,
    };
    priceCache.set(cacheKey, { price, change24h, timestamp: Date.now() });
    return result;
  } catch (error) {
    console.error(`[Price] Error fetching ${identifier}:`, error);
    return getFallbackPrice(identifier);
  }
}

function getFallbackPrice(identifier: string): TokenPriceData | null {
  let symbol = identifier;
  if (identifier.includes("0x") || identifier.length > 30) {
    symbol = getTokenSymbolFromAddress(identifier);
  }
  const fallback = FALLBACK_PRICES[symbol];
  if (fallback) {
    return {
      price: fallback.price,
      change24h: fallback.change24h,
      symbol,
      address: identifier,
    };
  }
  return null;
}

export async function refreshTokenPrice(
  identifier: string,
  network: string = "solana"
): Promise<TokenPriceData | null> {
  const cacheKey = `${network}:${identifier}`;
  priceCache.delete(cacheKey);
  return getTokenPrice(identifier, network);
}

export function clearPriceCache(): void {
  priceCache.clear();
}

export async function getMultipleTokenPrices(
  identifiers: string[],
  network: string = "solana"
): Promise<Record<string, TokenPriceData>> {
  const results: Record<string, TokenPriceData> = {};
  await Promise.all(
    identifiers.map(async (id) => {
      const priceData = await getTokenPrice(id, network);
      if (priceData) results[id] = priceData;
    })
  );
  return results;
}

export async function getTokenPriceBySymbol(
  symbol: string
): Promise<TokenPriceData | null> {
  const symbolToAddress: Record<
    string,
    { address: string; network: string }
  > = {
    SOL: { address: SOLANA_ADDRESSES.SOL, network: "solana" },
    USDC: { address: SOLANA_ADDRESSES.USDC, network: "solana" },
    USDT: { address: SOLANA_ADDRESSES.USDT, network: "solana" },
    FIXERCOIN: { address: SOLANA_ADDRESSES.FIXERCOIN, network: "solana" },
    LOCKER: { address: SOLANA_ADDRESSES.LOCKER, network: "solana" },
    FXM: { address: SOLANA_ADDRESSES.FXM, network: "solana" },
    PINGX: { address: SOLANA_ADDRESSES.PINGX, network: "solana" },
    ETH: {
      address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
      network: "ethereum",
    },
    BNB: {
      address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c",
      network: "bsc",
    },
    MATIC: {
      address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270",
      network: "polygon",
    },
  };
  const info = symbolToAddress[symbol];
  if (!info) return null;
  return getTokenPrice(info.address, info.network);
}

// ============================================================
// HOLDERS — via /api/rpc proxy (functions/api/rpc.ts)
// ============================================================

export async function getSolanaTokenHolders(mint: string): Promise<Holder[]> {
  if (!mint) return [];

  try {
    // 1) Largest token accounts for this mint
    const res = await fetch("/api/rpc", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: Date.now(),
        method: "getTokenLargestAccounts",
        params: [mint],
      }),
    });

    if (!res.ok) {
      console.warn("[Holders] /api/rpc returned", res.status);
      return [];
    }

    const json = await res.json();
    if (json?.error) {
      console.warn("[Holders] RPC error:", json.error);
      return [];
    }

    const accounts: any[] = json?.result?.value || [];
    if (!accounts.length) return [];

    const tokenAccountAddresses = accounts.map((a: any) => a.address);

    // 2) Resolve owners for those ATAs (jsonParsed → info.owner)
    let ownerAccounts: any[] = [];
    try {
      const ownerRes = await fetch("/api/rpc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: Date.now() + 1,
          method: "getMultipleAccounts",
          params: [tokenAccountAddresses, { encoding: "jsonParsed" }],
        }),
      });
      const ownerJson = await ownerRes.json();
      ownerAccounts = ownerJson?.result?.value || [];
    } catch (e) {
      console.warn("[Holders] owner lookup failed, falling back to ATAs:", e);
    }

    const total = accounts.reduce(
      (sum: number, a: any) =>
        sum + (parseFloat(a.uiAmountString || a.amount || "0") || 0),
      0
    );

    return accounts.map((a: any, i: number) => {
      const bal = parseFloat(a.uiAmountString || a.amount || "0") || 0;
      const ata = a.address || "";
      const owner = ownerAccounts[i]?.data?.parsed?.info?.owner;
      // Do not silently show ATAs as if they were wallets.
      const displayAddress = owner ? owner : `${ata} (TOKEN ACCOUNT)`;
      return {
        rank: i + 1,
        address: displayAddress,
        balance: bal,
        percentage: total > 0 ? (bal / total) * 100 : 0,
      };
    });
  } catch (e) {
    console.warn("[Holders] Solana holder fetch failed:", e);
    return [];
  }
}

export async function getTokenHolders(
  _mint: string,
  _chain?: string
): Promise<Holder[]> {
  return [];
}

// ============================================================
// TRADES — GeckoTerminal (keyless)
// ============================================================

const GECKO_NETWORK: Record<string, string> = {
  solana: "solana",
  ethereum: "eth",
  eth: "eth",
  bsc: "bsc",
  polygon: "polygon_pos",
  polygon_pos: "polygon_pos",
};

/**
 * Fetch recent trades + 24h volume via GeckoTerminal.
 *
 * Fix for the "SELL shows $96.35" bug:
 *  - GeckoTerminal's /trades endpoint returns FLAT fields
 *    `from_token_address`, `to_token_address`, `from_token_amount`,
 *    `to_token_amount`, `price_from_in_usd`, `price_to_in_usd`.
 *    It does NOT return `to_token.address` / `from_token.address`.
 *  - We check which side of the swap matches OUR mint, then use
 *    that side's amount AND that side's USD price. Never divide
 *    total / amount, and never pick the wrong side.
 *  - Pool selection: highest 24h USD volume (tie-break by reserve),
 *    so dust pools don't dictate the numbers.
 */
export async function getTokenTrades(
  mint: string,
  chain: string = "solana"
): Promise<{ trades: Trade[]; volume24h: number }> {
  const empty = { trades: [] as Trade[], volume24h: 0 };
  if (!mint) return empty;
  const network = GECKO_NETWORK[chain];
  if (!network) return empty;

  try {
    // 1. All pools for this token
    const poolsRes = await fetch(
      `https://api.geckoterminal.com/api/v2/networks/${network}/tokens/${mint}/pools`
    );
    if (!poolsRes.ok) return empty;
    const poolsJson = await poolsRes.json();

    const pools: any[] = poolsJson?.data || [];
    if (!pools.length) return empty;

    // 2. Pick highest 24h volume pool (tie-break: reserve)
    const scored = pools
      .map((p) => {
        const addr = p?.attributes?.address || p?.id?.split("_")?.pop() || "";
        const vol = parseFloat(p?.attributes?.volume_usd?.h24 || "0") || 0;
        const reserve = parseFloat(p?.attributes?.reserve_in_usd || "0") || 0;
        return { addr, vol, reserve };
      })
      .filter((p) => p.addr);

    scored.sort((a, b) => {
      if (b.vol !== a.vol) return b.vol - a.vol;
      return b.reserve - a.reserve;
    });

    const best = scored[0];
    if (!best) return empty;
    const volume24h = best.vol;

    // 3. Trades for the chosen pool
    const tradesRes = await fetch(
      `https://api.geckoterminal.com/api/v2/networks/${network}/pools/${best.addr}/trades`
    );
    if (!tradesRes.ok) return { trades: [], volume24h };
    const tradesJson = await tradesRes.json();
    const items: any[] = tradesJson?.data || [];

    const viewMint = mint.toLowerCase();

    const trades: Trade[] = items.slice(0, 8).map((t: any, i: number) => {
      const a = t.attributes || {};

      // Flat address fields — GeckoTerminal returns these, NOT
      // `to_token.address` / `from_token.address`.
      const fromAddr = (a.from_token_address || "").toLowerCase();
      const toAddr = (a.to_token_address || "").toLowerCase();

      let price = 0;
      let amount = 0;

      if (fromAddr === viewMint) {
        // Our token is the FROM side (e.g. SELL: token → SOL)
        price = parseFloat(a.price_from_in_usd || "0") || 0;
        amount = parseFloat(a.from_token_amount || "0") || 0;
      } else if (toAddr === viewMint) {
        // Our token is the TO side (e.g. BUY: SOL → token)
        price = parseFloat(a.price_to_in_usd || "0") || 0;
        amount = parseFloat(a.to_token_amount || "0") || 0;
      } else {
        // Unknown side: fall back to "from" side
        price = parseFloat(a.price_from_in_usd || "0") || 0;
        amount = parseFloat(a.from_token_amount || "0") || 0;
      }

      const total = parseFloat(a.volume_in_usd || "0") || 0;

      const timeStr = a.block_timestamp
        ? new Date(a.block_timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "N/A";

      return {
        id: a.tx_hash || `trade-${i}`,
        type: a.kind === "sell" ? "sell" : "buy",
        price,
        amount,
        total,
        timestamp: timeStr,
        maker: a.tx_from_address || "Unknown",
      };
    });

    return { trades, volume24h };
  } catch (e) {
    console.warn("[Trades] GeckoTerminal fetch failed:", e);
    return empty;
  }
}

// ============================================================
// Format helpers
// ============================================================

export function formatPrice(price: number): string {
  if (!price || price === 0) return "$0.00";
  if (price < 0.000001) return `$${price.toExponential(4)}`;
  if (price < 0.001) return `$${price.toFixed(8)}`;
  if (price < 1) return `$${price.toFixed(6)}`;
  if (price < 1000) return `$${price.toFixed(4)}`;
  return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function formatPercentage(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

export function formatLargeNumber(num: number): string {
  if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
  return num.toString();
}

// ============================================================
// Convenience bundle
// ============================================================

export const dexscreenerAPI = {
  getPriceByMint: getTokenPrice,
  getPricesByMints: getMultipleTokenPrices,
  getTokensByMints: async (mints: string[]) => {
    const prices = await getMultipleTokenPrices(mints);
    return Object.values(prices);
  },
  getPriceBySymbol: getTokenPriceBySymbol,
  getTrades: getTokenTrades,
  getHolders: getTokenHolders,
  getSolanaHolders: getSolanaTokenHolders,
};
